import * as React from 'react';
import styled from 'styled-components';
import { Text, Metrics, Colors } from '@origin-digital/style-guide';
import { Box } from '@origin-digital/layout';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Grid from '../../components/MuiGrid/Grid';
import { Recommendation } from '../../apps/SolarCalculator/components/CalculatorResult/CalculatorResult.types';
import {
  getFormattedTotalSystemCost,
  getBienniumMonthlySystemCost
} from '../../apps/SolarCalculator/components/CalculatorResult/ResultTab/ResultTabComponents/solarSavingsHelper';
import {
  RequestQuoteButton,
  IRequestQuoteProps
} from '../../apps/SolarCalculator/components/CalculatorResult/ResultTab/ResultTabComponents/RequestQuoteButton';

export interface IStickyBanner extends IRequestQuoteProps {
  selected: Recommendation;
}

const useStyles = makeStyles({
  appBar: {
    backgroundColor: Colors.WHITE,
    bottom: 0,
    boxShadow:
      '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    left: 0,
    minHeight: '120px',
    position: 'fixed',
    top: 'auto',
    width: '100%',
    zIndex: 1100
  },
  button: {
    minWidth: '112px'
  },
  fitContent: {
    width: 'auto'
  },
  link: {
    color: Colors.RED,
    textDecoration: 'underline'
  },
  marginTop: {
    marginTop: '10px'
  }
});

const LargerText = styled(Text)<{ smallerWidth: boolean }>`
  font-size: ${props =>
    props.smallerWidth ? Metrics.FontSizes.Large.H3 : Metrics.FontSizes.Large.H2}px;
  line-height: ${props => (props.smallerWidth ? Metrics.LineHeights.H3 : Metrics.LineHeights.H1)}px;
  margin-top: ${Metrics.Spacing.M_Y_XXS}px;
`;

const MaxContentText = styled(Text)`
  width: max-content;
`;

export const StickyBanner = (props: IStickyBanner) => {
  const theme = useTheme();
  const classes = useStyles();
  const { averageBill, postcode, usagePercentDuringDay, selected } = props;
  const { solarSaving, solarSystem } = selected;
  const ROI = solarSaving.payoffTimeYears;
  const formattedSystemCost = getFormattedTotalSystemCost(solarSystem.totalCost);
  const cost = getBienniumMonthlySystemCost(solarSystem.totalCost);
  const aboveMediumWidth = useMediaQuery(theme.breakpoints.up('lg'));
  const aboveSmallWidth = useMediaQuery(theme.breakpoints.up('md'));
  const aboveXSmallWidth = useMediaQuery(theme.breakpoints.up('sm'));
  const isXSmallWidth = useMediaQuery(theme.breakpoints.up('xs'));

  return (
    <Box className={classes.appBar}>
      <Box minHeight="inherit" display="flex">
        <Grid container direction="row" justify="center" alignContent="center">
          <Grid
            item
            direction="row"
            alignContent="center"
            justify="space-between"
            container
            xs={11}
            sm={10}
            md={10}
            lg={9}
            xl={7}
          >
            <Grid
              item
              direction="row"
              alignContent="center"
              container
              xs={9}
              sm={8}
              md={7}
              lg={7}
              xl={7}
            >
              <Grid
                item
                container
                direction="row"
                justify="center"
                style={{
                  width: isXSmallWidth && '59%'
                }}
              >
                <Grid
                  item
                  container
                  direction="column"
                  justify="center"
                  xs={11}
                  sm={'auto'}
                  md={'auto'}
                  className={classes.fitContent}
                >
                  <Grid item>
                    <LargerText color="grey56" smallerWidth={!aboveMediumWidth}>
                      ${cost}/m
                    </LargerText>
                    <Text size={aboveMediumWidth ? 'sm' : 'xs'} color="grey80">
                      Payment plan
                    </Text>
                  </Grid>
                </Grid>
                <Grid
                  item
                  container
                  direction="column"
                  justify="center"
                  xs={11}
                  sm={'auto'}
                  md={'auto'}
                  className={classes.fitContent}
                  style={{
                    marginLeft: aboveXSmallWidth ? Metrics.Spacing.M_Y_LG : 0
                  }}
                >
                  <LargerText color="grey56" smallerWidth={!aboveMediumWidth}>
                    {formattedSystemCost}
                  </LargerText>
                  <Text size={aboveMediumWidth ? 'sm' : 'xs'} color="grey80">
                    System cost
                  </Text>
                </Grid>
              </Grid>
              <Grid
                item
                container
                direction="column"
                justify={aboveSmallWidth ? 'center' : 'flex-start'}
                alignContent={aboveSmallWidth ? 'center' : 'flex-start'}
                style={{
                  marginLeft: aboveXSmallWidth ? '54px' : 0
                }}
                className={classes.fitContent}
              >
                <Grid item>
                  <Text size={aboveMediumWidth ? 'xl' : 'sm'} color="grey56">
                    {ROI} years
                  </Text>
                  <MaxContentText size={aboveMediumWidth ? 'sm' : 'xs'} color="grey80">
                    {aboveMediumWidth ? 'Payback period' : 'Payback'}
                  </MaxContentText>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="row"
              justify={aboveMediumWidth ? 'flex-start' : 'center'}
              xs={3}
              sm={4}
              md={5}
              lg={4}
              xl={3}
            >
              <Grid
                item
                container
                direction="column"
                justify="center"
                alignContent="center"
                className={classes.fitContent}
                xs={11}
                sm={11}
                md={'auto'}
              >
                <Grid item className={classes.button} data-id="sticky-banner-order">
                  <RequestQuoteButton
                    averageBill={averageBill}
                    postcode={postcode}
                    usagePercentDuringDay={usagePercentDuringDay}
                    parentDataId="sticky-banner-order-button"
                  />
                </Grid>
              </Grid>
              <Grid
                item
                container
                direction="column"
                justify="center"
                alignContent="center"
                className={classes.fitContent}
                style={{ marginLeft: aboveSmallWidth ? Metrics.Spacing.M_Y_LG : 0 }}
                xs={11}
                sm={11}
                md={'auto'}
              >
                <Grid
                  item
                  className={aboveSmallWidth ? '' : classes.marginTop}
                  data-id="sticky-banner-request-a-call"
                >
                  <RequestQuoteButton
                    averageBill={averageBill}
                    postcode={postcode}
                    usagePercentDuringDay={usagePercentDuringDay}
                    isButton={false}
                    parentDataId="sticky-banner-request-a-call"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
